import {
  createFieldLimitedAggFunc,
  equalityAggFunc,
  SettlementInstructionDirectionEnum,
  useConstant,
  type Column,
  type ColumnDef,
} from '@talos/kyoko';
import type {
  GridOptions,
  IAggFuncParams,
  KeyCreatorParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { getColumnTitle, getColumnTooltip } from '../../../../utils/fieldDefinitionUtils';
import { useActiveBatchesFieldDefinitions } from '../ActiveBatches/useActiveBatchesFieldDefinitions';
import type { useSettleBatchContext } from '../SettleBatchContext';
import { createDirectionalAmountColumn } from '../utils/createDirectionalAmountColumn';
import { getDetailedLPAssetStatus } from '../utils/lpAssetStatusMapping';
import { getLPBatchStatusMapping } from '../utils/lpBatchStatusMapping';
import { getCreatedByColumnDisplayValue } from '../utils/lpBatchUtils';
import { getDirectionDisplayLabel } from '../utils/lpSettlementInstructionActions';
import type { LPSettlementInstructionRow } from './LPSettlementBatchesGrid';
import {
  BatchCounterpartyCell,
  BatchCreationUserCell,
  BatchDetailsButtonCell,
  BatchIDCell,
  BatchProgressCell,
  BatchReportCountCell,
  CombinedStatusCell,
  SettlementSubAccountsCell,
} from './LPSettlementBatchesGridCellRenderers';

const LP_BATCH_STATUS_AGG_FUNC = 'lpBatchStatus';
const LP_ASSET_FIELD_EQUALITY_AGG_FUNC = 'lpAssetFieldEquality';
const LP_BATCH_ID_FIELD_EQUALITY_AGG_FUNC = 'lpBatchIdFieldEquality';

function getSettlementSubAccountsValue(
  params: ValueGetterParams<LPSettlementInstructionRow> | KeyCreatorParams<LPSettlementInstructionRow>
) {
  const { data, context } = params;
  return (
    data?.BatchSettlementSubAccounts?.map(
      (account: { SubAccount: string }) =>
        context.current.subAccountsByName?.get(account.SubAccount)?.DisplayName ?? account.SubAccount
    )
      .toSorted()
      .join(', ') || ''
  );
}

export function useLPSettlementBatchesGridColumns(
  openBatchDetails: ReturnType<typeof useSettleBatchContext>['openBatchDetails'],
  mode: 'activeBatches' | 'batchHistory'
) {
  const activeBatchesFieldDefinitions = useActiveBatchesFieldDefinitions();
  const theme = useTheme();
  const aggFuncs = useMemo<GridOptions<LPSettlementInstructionRow>['aggFuncs']>(
    () => ({
      [LP_BATCH_STATUS_AGG_FUNC]: (params: IAggFuncParams<LPSettlementInstructionRow>) => {
        if (params.rowNode.field === 'Asset') {
          return createFieldLimitedAggFunc('Asset', equalityAggFunc)(params);
        }
        if (params.rowNode.field === 'SettleBatchID') {
          // return the batch status of the first child (this will always be the same for all children)
          return params.rowNode.allLeafChildren?.[0]?.data?.BatchStatus;
        }
        return '';
      },
      [LP_ASSET_FIELD_EQUALITY_AGG_FUNC]: createFieldLimitedAggFunc('Asset', equalityAggFunc),
      [LP_BATCH_ID_FIELD_EQUALITY_AGG_FUNC]: createFieldLimitedAggFunc('SettleBatchID', equalityAggFunc),
    }),
    []
  );

  const columns = useConstant<Column[]>([
    // Core data columns (visible by default, can be dragged to group panel)
    {
      field: 'MarketAccount',
      type: 'marketAccount',
      title: getColumnTitle(activeBatchesFieldDefinitions.marketAccount),
      description: getColumnTooltip(activeBatchesFieldDefinitions.marketAccount),
      initialWidth: 140,
      enableRowGroup: true,
      initialRowGroup: true,
      initialRowGroupIndex: 0,
    },
    {
      field: 'SettleDeadline',
      title: getColumnTitle(activeBatchesFieldDefinitions.settlementDue),
      description: getColumnTooltip(activeBatchesFieldDefinitions.settlementDue),
      type: 'date',
      initialWidth: 140,
      enableRowGroup: true,
      initialRowGroup: true,
      initialRowGroupIndex: 1,
    },
    {
      field: 'SettleBatchID',
      title: getColumnTitle(activeBatchesFieldDefinitions.batchId),
      description: getColumnTooltip(activeBatchesFieldDefinitions.batchId),
      type: 'custom',
      initialSort: '+',
      initialWidth: 140,
      enableRowGroup: true,
      initialRowGroup: true,
      initialRowGroupIndex: 2,
      params: {
        cellRenderer: BatchIDCell,
      },
    },
    {
      field: 'Asset',
      type: 'asset',
      title: getColumnTitle(activeBatchesFieldDefinitions.asset),
      description: getColumnTooltip(activeBatchesFieldDefinitions.asset),
      initialWidth: 100,
      enableRowGroup: true,
      initialRowGroup: true,
      initialRowGroupIndex: 3,
    },

    {
      field: 'Status',
      title: getColumnTitle(activeBatchesFieldDefinitions.status),
      description: getColumnTooltip(activeBatchesFieldDefinitions.status),
      type: 'custom',
      initialWidth: 150,
      aggregate: true,
      enableRowGroup: true,
      initialAggFunc: LP_BATCH_STATUS_AGG_FUNC,
      params: {
        cellRenderer: CombinedStatusCell,
        // change the value getter so the comparator resolves the sorted data
        valueGetter: ({ node, data }: ValueGetterParams<LPSettlementInstructionRow>) => {
          if (!node || !data) {
            return '';
          }

          if (node.group && node.field === 'SettleBatchID') {
            const batchData = node.allLeafChildren?.[0]?.data;
            if (batchData?.BatchStatus) {
              const statusMapping = getLPBatchStatusMapping(batchData.BatchStatus, theme);
              return statusMapping?.label ?? batchData.BatchStatus;
            }
          }

          if (!node.group && data.Status) {
            const detailedStatus = getDetailedLPAssetStatus(data.Status, data.Direction);
            return detailedStatus ?? data.Status;
          }

          return data?.Status ?? '';
        },
      },
    },
    {
      field: 'Direction',
      type: 'custom',
      title: getColumnTitle(activeBatchesFieldDefinitions.direction),
      description: getColumnTooltip(activeBatchesFieldDefinitions.direction),
      initialWidth: 100,
      enableRowGroup: true,
      initialAggFunc: LP_ASSET_FIELD_EQUALITY_AGG_FUNC,
      params: {
        valueFormatter: ({ value }: ValueFormatterParams<LPSettlementInstructionRow>): string => {
          if (!value) {
            return '';
          }
          return getDirectionDisplayLabel(value);
        },
      },
    },
    {
      field: 'BatchSettlementSubAccounts',
      title: getColumnTitle(activeBatchesFieldDefinitions.subAccount),
      description: getColumnTooltip(activeBatchesFieldDefinitions.subAccount),
      type: 'custom',
      initialWidth: 140,
      enableRowGroup: true,
      aggregate: true,
      initialAggFunc: LP_BATCH_ID_FIELD_EQUALITY_AGG_FUNC,
      params: {
        valueGetter: getSettlementSubAccountsValue,
        keyCreator: getSettlementSubAccountsValue,
        cellRenderer: SettlementSubAccountsCell,
      },
    },
    {
      field: 'BatchPercentCompleted',
      title: getColumnTitle(activeBatchesFieldDefinitions.batchProgress),
      description: getColumnTooltip(activeBatchesFieldDefinitions.batchProgress),
      type: 'custom',
      initialWidth: 120,
      enableRowGroup: false,
      aggregate: true,
      sortable: true,
      params: {
        cellRenderer: BatchProgressCell,
        valueGetter: ({ data, node }: ValueGetterParams<LPSettlementInstructionRow>) => {
          if (!node) {
            return 0;
          }
          if (node.group && node.field === 'SettleBatchID') {
            const batchData = node.allLeafChildren?.[0]?.data;
            const value = batchData?.BatchPercentCompleted;
            return value ? parseFloat(value) : 0;
          }
        },
      },
    },
    createDirectionalAmountColumn({
      id: 'Outgoing',
      fieldDefinition: activeBatchesFieldDefinitions.outgoing,
      direction: SettlementInstructionDirectionEnum.Outbound,
      initialWidth: 120,
    }),
    createDirectionalAmountColumn({
      id: 'Incoming',
      fieldDefinition: activeBatchesFieldDefinitions.incoming,
      direction: SettlementInstructionDirectionEnum.Inbound,
      initialWidth: 120,
    }),

    {
      field: 'UnsettledAmount',
      title: getColumnTitle(activeBatchesFieldDefinitions.unsettledAmount),
      description: getColumnTooltip(activeBatchesFieldDefinitions.unsettledAmount),
      type: 'size',
      initialWidth: 140,
      aggregate: true,
      params: {
        currencyField: 'Asset',
        showInTermsOfContracts: true,
        aggregateIf: (params: IAggFuncParams) => params.rowNode.field === 'Asset',
      },
      enableRowGroup: true,
    },

    {
      field: 'UnsettledEquivalentAmount',
      title: getColumnTitle(activeBatchesFieldDefinitions.unsettledAmountHome),
      description: getColumnTooltip(activeBatchesFieldDefinitions.unsettledAmountHome),
      type: 'size',
      aggregate: true,
      params: {
        currencyField: 'EquivalentCurrency',
        aggregateIf: (params: IAggFuncParams) => params.rowNode.field === 'Asset',
      },
    },
    {
      field: 'BatchReportCount',
      title: getColumnTitle(activeBatchesFieldDefinitions.totalTransactions),
      description: getColumnTooltip(activeBatchesFieldDefinitions.totalTransactions),
      initialWidth: 120,
      type: 'custom',
      headerClass: 'ag-right-aligned-header',
      cellClass: 'ag-right-aligned-cell',
      params: {
        cellRenderer: BatchReportCountCell,
      },
    },
    {
      field: 'BatchCreationUser',
      title: getColumnTitle(activeBatchesFieldDefinitions.createdBy),
      description: getColumnTooltip(activeBatchesFieldDefinitions.createdBy),
      type: 'custom',
      initialWidth: 120,
      enableRowGroup: true,
      aggregate: true,
      initialAggFunc: LP_BATCH_ID_FIELD_EQUALITY_AGG_FUNC,
      params: {
        valueGetter: ({ data }: ValueGetterParams<LPSettlementInstructionRow>) =>
          data ? getCreatedByColumnDisplayValue(data) : '',
        keyCreator: ({ data }: KeyCreatorParams<LPSettlementInstructionRow>) =>
          data ? getCreatedByColumnDisplayValue(data) : 'Unknown',
        cellRenderer: BatchCreationUserCell,
      },
    },

    {
      field: 'BatchCounterparty',
      title: getColumnTitle(activeBatchesFieldDefinitions.counterparty),
      description: getColumnTooltip(activeBatchesFieldDefinitions.counterparty),
      type: 'custom',
      initialWidth: 120,
      enableRowGroup: true,
      aggregate: true,
      initialAggFunc: LP_BATCH_ID_FIELD_EQUALITY_AGG_FUNC,
      params: {
        cellRenderer: BatchCounterpartyCell,
      },
    },
    {
      field: 'BatchSettleCompletion',
      title: getColumnTitle(activeBatchesFieldDefinitions.settlementComplete),
      description: getColumnTooltip(activeBatchesFieldDefinitions.settlementComplete),
      type: 'date',
      initialWidth: 140,
      enableRowGroup: true,
      initialHide: mode === 'activeBatches',
    },
    { field: 'Timestamp', type: 'date', initialWidth: 140, enableRowGroup: true, initialHide: true },

    // Instruction-specific columns (each row is now an instruction)
    { field: 'InstructionID', initialHide: true, type: 'text', initialWidth: 140, enableRowGroup: true },

    { field: 'MarketTransferStatus', initialHide: true, type: 'text', initialWidth: 160 },

    {
      type: 'textArray',
      field: 'LatestTransferDetails.SourceData.BlockchainIDs',
      initialHide: true,
      title: 'Transaction ID',
    },

    // Action column
    {
      id: 'edit',
      type: 'custom',
      initialPinned: 'right',
      initialWidth: 40,
      minWidth: 40,
      maxWidth: 40,
      suppressColumnsToolPanel: true,
      suppressHeaderMenuButton: true,
      frozen: true,
      params: {
        resizable: false,
        suppressAutoSize: true,
        cellRenderer: BatchDetailsButtonCell,
        cellRendererParams: {
          openBatchDetails,
        },
      },
    },
  ] satisfies ColumnDef<LPSettlementInstructionRow>[]);

  return { columns, aggFuncs };
}
